import styled from 'styled-components'
import axios from 'axios'
import { useSelector, useDispatch, } from 'react-redux'
import { useParams, useLocation, useNavigate, } from 'react-router-dom'
import React, { useRef, useMemo, useEffect, useCallback, useState, } from 'react'

import {
  Header, AgentList,
  AgentListMap, DashboardUtils, AgentDetail,
  ControlRoomRequestingSidebar,
} from '@systems'

import { RootState, getAgents, searchAgents, loadMoreAgents, loadMoreSearchAgents, } from '@reducers'

import { AGENT_LIST_PAGE_SIZE, AGENT_POLLING_INTERVAL_MS, urls, } from '@globals'

import { Agent, } from '@types'

type ControlRoomChatUnreadCount = {
  agent_id: number
  unread_count: number
}

const normalizeChatUnreadCounts = (data: unknown): Array<ControlRoomChatUnreadCount> => {
  if(Array.isArray(data)) {
    return data
  }

  return []
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .map-container {
    display: flex;
    flex: 1;
    position: relative;
    min-height: 0;
    overflow: hidden;

    #map {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .panel-overlay {
      position: absolute;
      inset: 0;
      z-index: 100;
      pointer-events: none;
    }

    .panel-overlay > *,
    .panel-strip > * {
      pointer-events: auto;
    }

    .panel-strip {
      display: contents;
    }

    ${ AgentList } {
      position: absolute;
      width: 350px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      background-color: rgba(255, 255, 255, 0.96);
      align-self: center;
      border-radius: 5px;
      z-index: 100;
      border-radius: 4px;
      overflow: hidden;
    }

    ${ DashboardUtils } {
      position: absolute;
      width: 350px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      height: 50px;
      background-color: rgba(255, 255, 255, 0.85);
      align-self: center;
      border-radius: 5px;
      z-index: 100;
      border-radius: 4px;
      overflow: hidden;
      top: 24px;
      left: 40px;
    }

    ${ AgentDetail } {
      position: absolute;
      width: 300px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      background-color: rgba(255, 255, 255, 0.85);
      align-self: center;
      border-radius: 5px;
      z-index: 100;
      border-radius: 4px;
      overflow: hidden;
      top: 86px;
      left: 420px;
    }

    ${ AgentList } {
      height: calc(100% - 110px);
      top: 86px;
      left: 40px;
    }

    ${ ControlRoomRequestingSidebar } {
      position: absolute;
      top: 86px;
      right: 40px;
      height: calc(100% - 110px);
      z-index: 100;
    }

    @media (max-width: 980px) {
      .panel-strip {
        position: absolute;
        top: 86px;
        right: 16px;
        bottom: 16px;
        left: 16px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        overflow: auto;
      }

      ${ DashboardUtils },
      ${ AgentList },
      ${ AgentDetail },
      ${ ControlRoomRequestingSidebar } {
        position: relative;
        top: auto;
        left: auto;
        right: auto;
        align-self: flex-start;
        flex-shrink: 0;
        max-width: calc(100vw - 32px);
      }

      ${ DashboardUtils } {
        position: absolute;
        top: 16px;
        left: 16px;
        width: min(350px, calc(100vw - 32px));
      }

      ${ AgentList } {
        width: min(350px, calc(100vw - 32px));
        height: min(620px, calc(100vh - 130px));
      }

      ${ AgentDetail } {
        width: min(300px, calc(100vw - 32px));
      }

      ${ ControlRoomRequestingSidebar } {
        width: min(380px, calc(100vw - 32px));
        height: min(720px, calc(100vh - 130px));
      }
    }

    @media (max-width: 760px) {
      .panel-strip {
        top: 84px;
        flex-direction: column;
      }

      ${ DashboardUtils },
      ${ AgentList },
      ${ AgentDetail },
      ${ ControlRoomRequestingSidebar } {
        width: calc(100vw - 32px);
      }

      ${ AgentList },
      ${ ControlRoomRequestingSidebar } {
        height: min(520px, calc(100vh - 120px));
      }
    }
  }
`

function MainControllMap() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { agentId: selectedAgentId, } = useParams()
  const defaultAgentPagination = useMemo(
    () => ({
      count: 0,
      next: '',
      previous: '',
    }),
    []
  )

  const agents = useSelector((state: RootState) => state.userManagement.agents)
  const token = useSelector((state: RootState) => state.account.token)
  const agentPagination = useSelector(
    (state: RootState) => state.userManagement.agentPagination || defaultAgentPagination
  )
  const includeEndOfWorkingAgents = useSelector((state: RootState) => state.appState.data.includeEndOfWorkingAgents)

  const map = useRef<naver.maps.Map>()
  const agentCountRef = useRef(0)
  const [ unreadChatCountByAgentId, setUnreadChatCountByAgentId, ] = useState<Record<number, number>>({})

  const agentSearchValue = useMemo(
    () => {
      const urlParams = new URLSearchParams(location.search)
      return urlParams.get('search-agent')
    },
    [ location, ]
  )

  const fetchAgents = useCallback(
    (append = false, offset = 0, limit = AGENT_LIST_PAGE_SIZE) => {
      if(agentSearchValue) {
        dispatch(searchAgents({
          offset,
          limit,
        }, agentSearchValue, append))
      } else {
        dispatch(getAgents({
          offset,
          limit,
        }, includeEndOfWorkingAgents, append))
      }
    },
    [ agentSearchValue, includeEndOfWorkingAgents, ]
  )

  const fetchUnreadChatCounts = useCallback(
    async () => {
      if(!token) {
        setUnreadChatCountByAgentId({})
        return
      }

      try {
        const response = await axios.get<unknown>(
          urls.users.CONTROL_ROOM_CHAT_UNREAD_COUNTS,
          {
            headers: {
              Authorization: `Bearer ${ token }`,
            },
          }
        )

        const unreadCounts = normalizeChatUnreadCounts(response.data)
        const nextUnreadChatCountByAgentId = unreadCounts.reduce<Record<number, number>>(
          (result, unreadCount) => ({
            ... result,
            [unreadCount.agent_id]: unreadCount.unread_count,
          }),
          {}
        )

        setUnreadChatCountByAgentId(nextUnreadChatCountByAgentId)
      } catch (error) {
        return
      }
    },
    [ token, ]
  )

  useEffect(
    () => {
      agentCountRef.current = agents.length
    },
    [ agents.length, ]
  )

  useEffect(
    () => {
      fetchAgents()
    },
    [ fetchAgents, ]
  )

  useEffect(
    () => {
      fetchUnreadChatCounts()

      const unreadChatCountInterval = setInterval(
        fetchUnreadChatCounts,
        AGENT_POLLING_INTERVAL_MS
      )

      return () => {
        clearInterval(unreadChatCountInterval)
      }
    },
    [ fetchUnreadChatCounts, ]
  )

  useEffect(
    () => {
      const autoRefreshAgentsInterval = setInterval(
        () => fetchAgents(false, 0, Math.max(agentCountRef.current || 0, AGENT_LIST_PAGE_SIZE)),
        AGENT_POLLING_INTERVAL_MS
      )

      return () => {
        clearInterval(autoRefreshAgentsInterval)
      }
    },
    [ fetchAgents, ]
  )

  const onAgentsEndReached = useCallback(
    () => {
      if(!agentPagination.next) {
        return
      }

      if(agentSearchValue) {
        dispatch(loadMoreSearchAgents({
          offset: agents.length,
          limit: AGENT_LIST_PAGE_SIZE,
        }, agentSearchValue))
      } else {
        dispatch(loadMoreAgents({
          offset: agents.length,
          limit: AGENT_LIST_PAGE_SIZE,
        }, includeEndOfWorkingAgents))
      }
    },
    [ agentPagination.next, agents.length, agentSearchValue, includeEndOfWorkingAgents, ]
  )

  const onAgentChatRead = useCallback(
    (agentId: number) => {
      setUnreadChatCountByAgentId(previousUnreadChatCountByAgentId => {
        if(!previousUnreadChatCountByAgentId[agentId]) {
          return previousUnreadChatCountByAgentId
        }

        return {
          ... previousUnreadChatCountByAgentId,
          [agentId]: 0,
        }
      })
    },
    []
  )

  const onAgentSelect = useCallback(
    (agent: Agent) => () => {
      if(agent.id.toString() !== selectedAgentId) {
        navigate(`/agents/${ agent.id }`)
      }

      if(map.current) {
        map.current.setZoom(14, false)
        map.current.panTo(new naver.maps.LatLng(
          agent.agent_location.coord.latitude,
          agent.agent_location.coord.longitude
        ), { duration: 500, })
      }
    },
    [ selectedAgentId, ]
  )

  return (
    <Container>
      <Header />
      <div className='map-container'>
        <AgentListMap
          agents={ agents }
          selectedAgentId={ selectedAgentId }
          onAgentSelect={ onAgentSelect }
          map={ map.current }
          setMap={ (newMap) => map.current = newMap }
        />
        <div className='panel-overlay'>
          <DashboardUtils />
          <div className='panel-strip'>
            <AgentList
              agents={ agents }
              selectedAgentId={ selectedAgentId }
              onAgentSelect={ onAgentSelect }
              onEndReached={ onAgentsEndReached }
              unreadChatCountByAgentId={ unreadChatCountByAgentId }
            />
            <ControlRoomRequestingSidebar />
            <AgentDetail
              agents={ agents }
              onAgentChatRead={ onAgentChatRead }
            />
          </div>
        </div>
      </div>
    </Container>
  )
}

export default MainControllMap
